import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Backdrop,
} from "@mui/material";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import { API_URL } from "../../utils/config";
import { canSendReport } from "../../utils/auth";
import { v4 as uuidv4 } from "uuid";

const SNAPSHOT_FIELDS = [
  { key: "utility_code", label: "Utility Code" },
  { key: "utility_name", label: "Utility Name" },
  { key: "alias", label: "Alias" },
  { key: "state", label: "State" },
  { key: "commodity", label: "Commodity" },
  { key: "class", label: "Class" },
  { key: "last_ptc_update", label: "Last PTC UP" },
  { key: "has_start_date", label: "Has Start Date" },
  { key: "has_end_date", label: "Has End Date" },
  { key: "exact_website", label: "Exact Website" },
  { key: "graduated_standard_rate", label: "Graduated Standard Rate" },
  { key: "sptc_rate", label: "SPTC Rate" },
  { key: "uom", label: "UOM" },
  { key: "last_scanned", label: "Last Scanned" },
  { key: "source_url", label: "Source URL" },
];

const SendMonthlySnapshotReportConfirm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const normalizeEmail = (value) =>
    value
      ?.split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ") ?? "";

  useEffect(() => {
    const checkUser = async () => {
      try {
        const allowed = await canSendReport();
        setShowButton(allowed);
      } catch (error) {
        console.error("Error checking user:", error);
        setShowButton(false);
      }
    };
    checkUser();
  }, []);

  useEffect(() => {
    const fetchSnapshotData = async () => {
      try {
        setLoading(true);
        const session = await fetchAuthSession();
        if (!session.tokens?.accessToken) {
          throw new Error("No access token available");
        }
        const token = session.tokens.accessToken.toString();
        const res = await fetch(`${API_URL}/ptc/monthly_report_list`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        const raw = data.response ?? data.rate_changes_raw ?? (Array.isArray(data) ? data : []);
        const normalized = Array.isArray(raw)
          ? raw.map((item) => ({ id: uuidv4(), ...item }))
          : [];
        setRows(normalized);
      } catch (error) {
        console.error("Error fetching snapshot data:", error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSnapshotData();
  }, []);

  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleSend = async () => {
    const trimmedEmail = normalizeEmail(email);
    if (!trimmedEmail) {
      setMessage("Please enter at least one email address");
      setIsErrorMessage(true);
      setSnackbarOpen(true);
      return;
    }

    setSending(true);
    try {
      const allowed = await canSendReport();
      if (!allowed) {
        throw new Error("You do not have permission to send this report");
      }

      const session = await fetchAuthSession();
      if (!session.tokens?.accessToken) {
        throw new Error("No access token available");
      }
      const token = session.tokens.accessToken.toString();

      const response = await fetch(`${API_URL}/ptc/send_monthly_report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const data = await response.json().catch(() => ({}));
      const isError = data.status !== "1";
      setIsErrorMessage(isError);
      setMessage(
        data.message ||
          (isError ? "Failed to send, please try again later" : "Report sent successfully!")
      );
      setSnackbarOpen(true);

      if (!isError) {
        setTimeout(() => navigate(-1), 800);
      }
    } catch (error) {
      console.error("Error sending report:", error);
      setMessage(error.message || "Failed to send, please try again later");
      setIsErrorMessage(true);
      setSnackbarOpen(true);
    } finally {
      setSending(false);
    }
  };

  if (!showButton) {
    navigate(-1);
    return null;
  }

  return (
    <Box className="w-full min-h-[100vh] bg-[#EAF4FC]">
      <Header />
      <div className="w-full flex justify-center pt-10 pb-10">
        <div className="w-[92%] bg-white rounded shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-6">
            Send Monthly Snapshot Report — Confirm & Send
          </h2>

          <Box className="mb-4">
            <TextField
              fullWidth
              label="Email"
              type="text"
              variant="outlined"
              multiline
              minRows={1}
              maxRows={15}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={(e) => setEmail(normalizeEmail(e.target.value))}
              disabled={sending}
              placeholder="Separate multiple emails with comma"
            />
          </Box>

          <TableContainer component={Paper} className="mb-6">
            {loading ? (
              <div className="flex justify-center py-16">
                <CircularProgress />
              </div>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {SNAPSHOT_FIELDS.map(({ key, label }) => (
                      <TableCell
                        key={key}
                        align="left"
                        sx={{
                          fontWeight: 600,
                          height: 48,
                          ...(key === "utility_name" || key === "source_url" ? { minWidth: 200 } : {}),
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={SNAPSHOT_FIELDS.length} align="center">
                        No data
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row) => (
                      <TableRow key={row.id}>
                        {SNAPSHOT_FIELDS.map(({ key }) => (
                          <TableCell
                            key={key}
                            align="left"
                            sx={{
                              height: 48,
                              ...(key === "utility_name" || key === "source_url" ? { minWidth: 200 } : {}),
                            }}
                          >
                            {String(row[key] ?? "")}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
            {!loading && rows.length > 0 && (
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Rows per page:"
              />
            )}
          </TableContainer>

          <Box className="flex justify-center gap-4">
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSend}
              disabled={loading || sending}
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {sending ? "Sending…" : "Send"}
            </Button>
          </Box>
        </div>
      </div>

      <Backdrop
        sx={(theme) => ({
          color: "#fff",
          zIndex: theme.zIndex.modal + 1,
        })}
        open={sending}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={isErrorMessage ? "error" : "success"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SendMonthlySnapshotReportConfirm;
